import { Link, useNavigate } from "react-router-dom";
import { buildAuthOverlayUrl } from "../../utils/authNavigation";
import nemaCareLogo from "../../assets/design/nema-care-logo.png";
import ScrollToTopButton from "../shared/ScrollToTopButton";
import "./LandingSiteFooter.css";

const FAMILY_LINKS = [
  { id: "find", label: "Find Caregivers", type: "scroll", target: "caregivers" },
  { id: "hiw", label: "How It Works", type: "scroll", target: "how-it-works" },
  { id: "pricing", label: "Pricing & Plans", type: "route", to: "/pricing" },
  { id: "cm", label: "Care Manager Services", type: "scroll", target: "care-manager" },
  { id: "faqs", label: "FAQs", type: "scroll", target: "faqs" },
];

const PROFESSIONAL_LINKS = [
  { id: "become-cg", label: "Become a Caregiver", type: "route", to: "/become-caregiver" },
  {
    id: "become-cm",
    label: "Become a Care Manager",
    type: "auth",
    opts: { tab: "create", role: "manager", returnTo: "/care-manager" },
  },
  {
    id: "login-cg",
    label: "Caregiver Login",
    type: "auth",
    opts: { returnTo: "/caregiver", role: "caregiver" },
  },
  {
    id: "login-cm",
    label: "Care Manager Login",
    type: "auth",
    opts: { returnTo: "/care-manager", role: "manager" },
  },
];

const COMPANY_LINKS = [
  { id: "about", label: "About Us", href: "mailto:support@nemacarehub.com?subject=About%20NEMA%20Care" },
  { id: "mission", label: "Our Mission", href: "mailto:support@nemacarehub.com?subject=Our%20Mission" },
  { id: "careers", label: "Careers", href: "mailto:support@nemacarehub.com?subject=Careers" },
  { id: "blog", label: "Blog", href: "mailto:support@nemacarehub.com?subject=Blog" },
  { id: "contact", label: "Contact Us", href: "mailto:support@nemacarehub.com" },
];

const SUPPORT_LINKS = [
  { id: "help", label: "Help Center", href: "mailto:support@nemacarehub.com?subject=Help%20Center" },
  { id: "safety", label: "Safety & Security", type: "route", to: "/safety-security" },
  { id: "privacy", label: "Privacy Policy", type: "route", to: "/privacy-policy" },
  { id: "terms", label: "Terms of Service", type: "route", to: "/terms-of-use" },
  { id: "refund", label: "Refund Policy", type: "route", to: "/refund-policy" },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path
        d="M6.5 4h3l1.5 4-2 1.5a12 12 0 006 6L16.5 13.5l4 1.5v3A2 2 0 0118.5 20 14.5 14.5 0 014 5.5 2 2 0 016.5 4z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m4 7 8 6 8-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M12 21s7-5.4 7-11a7 7 0 10-14 0c0 5.6 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SocialIcon({ name }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "currentColor",
    "aria-hidden": true,
  };
  if (name === "facebook") {
    return (
      <svg {...common}>
        <path d="M14 9h3V6h-3c-2.2 0-4 1.8-4 4v2H8v3h2v7h3v-7h2.6l.4-3H13v-2c0-.6.4-1 1-1z" />
      </svg>
    );
  }
  if (name === "instagram") {
    return (
      <svg {...common}>
        <path d="M7 3h10a4 4 0 014 4v10a4 4 0 01-4 4H7a4 4 0 01-4-4V7a4 4 0 014-4zm5 4.5A4.5 4.5 0 1016.5 12 4.5 4.5 0 0012 7.5zm5.75-.9a1.1 1.1 0 11-1.1 1.1 1.1 1.1 0 011.1-1.1z" />
      </svg>
    );
  }
  if (name === "linkedin") {
    return (
      <svg {...common}>
        <path d="M6.5 9H3.7v11.3h2.8zm-1.4-4.5a1.65 1.65 0 11-1.65 1.65A1.65 1.65 0 015.1 4.5zM20.3 13.1c0-3.1-1.7-4.5-3.9-4.5a3.4 3.4 0 00-3 1.6V9H10.6v11.3h2.8v-6.1c0-1.6.3-3.1 2.3-3.1s2 1.5 2 3.1v6.1h2.6z" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M23 7.2a7.4 7.4 0 01-2.1.6 3.7 3.7 0 001.6-2 7.4 7.4 0 01-2.3.9 3.7 3.7 0 00-6.3 3.4A10.5 10.5 0 013.2 5.6a3.7 3.7 0 001.1 4.9 3.6 3.6 0 01-1.7-.5v.1a3.7 3.7 0 003 3.6 3.7 3.7 0 01-1.7.1 3.7 3.7 0 003.4 2.6A7.4 7.4 0 012 18.4a10.5 10.5 0 005.7 1.7c6.8 0 10.5-5.6 10.5-10.5v-.5A7.4 7.4 0 0023 7.2z" />
    </svg>
  );
}

function FooterLinkList({ title, children }) {
  return (
    <div className="site-footer__col">
      <h3 className="site-footer__col-title">{title}</h3>
      <ul className="site-footer__list">{children}</ul>
    </div>
  );
}

export default function LandingSiteFooter() {
  const navigate = useNavigate();

  function handleScroll(target) {
    scrollToId(target);
  }

  function handleAuth(opts) {
    navigate(buildAuthOverlayUrl("/", opts));
  }

  return (
    <footer className="site-footer" aria-label="Site footer">
      <ScrollToTopButton />
      <div className="site-footer__inner">
        <div className="site-footer__brand">
          <Link to="/" className="site-footer__logo" aria-label="NEMA Care Hub">
            <img src={nemaCareLogo} alt="NEMA Care Hub" />
          </Link>
          <p className="site-footer__tagline">
            India&apos;s first direct-hire home care marketplace. Hire directly, pay directly.
          </p>
          <div className="site-footer__social" aria-label="Social media">
            {[
              { id: "facebook", label: "Facebook" },
              { id: "instagram", label: "Instagram" },
              { id: "linkedin", label: "LinkedIn" },
              { id: "youtube", label: "YouTube" },
            ].map((item) => (
              <a
                key={item.id}
                className="site-footer__social-btn"
                href={
                  item.id === "linkedin"
                    ? "https://www.linkedin.com"
                    : item.id === "youtube"
                      ? "https://www.youtube.com"
                      : `https://www.${item.id}.com`
                }
                target="_blank"
                rel="noopener noreferrer"
                aria-label={item.label}
              >
                <SocialIcon name={item.id} />
              </a>
            ))}
          </div>
        </div>

        <FooterLinkList title="For Families">
          {FAMILY_LINKS.map((item) => (
            <li key={item.id}>
              {item.type === "route" ? (
                <Link to={item.to}>{item.label}</Link>
              ) : (
                <button type="button" onClick={() => handleScroll(item.target)}>
                  {item.label}
                </button>
              )}
            </li>
          ))}
        </FooterLinkList>

        <FooterLinkList title="For Professionals">
          {PROFESSIONAL_LINKS.map((item) => (
            <li key={item.id}>
              {item.type === "route" ? (
                <Link to={item.to}>{item.label}</Link>
              ) : (
                <button type="button" onClick={() => handleAuth(item.opts)}>
                  {item.label}
                </button>
              )}
            </li>
          ))}
        </FooterLinkList>

        <FooterLinkList title="Company">
          {COMPANY_LINKS.map((item) => (
            <li key={item.id}>
              <a href={item.href}>{item.label}</a>
            </li>
          ))}
        </FooterLinkList>

        <FooterLinkList title="Support">
          {SUPPORT_LINKS.map((item) => (
            <li key={item.id}>
              {item.type === "route" ? (
                <Link to={item.to}>{item.label}</Link>
              ) : (
                <a href={item.href}>{item.label}</a>
              )}
            </li>
          ))}
        </FooterLinkList>

        <div className="site-footer__col site-footer__col--contact">
          <h3 className="site-footer__col-title">Get in Touch</h3>
          <ul className="site-footer__contact">
            <li>
              <span className="site-footer__contact-icon" aria-hidden>
                <PhoneIcon />
              </span>
              <a href="tel:+917042220515">+91 70422 20515</a>
            </li>
            <li>
              <span className="site-footer__contact-icon" aria-hidden>
                <MailIcon />
              </span>
              <a href="mailto:support@nemacarehub.com">support@nemacarehub.com</a>
            </li>
            <li>
              <span className="site-footer__contact-icon" aria-hidden>
                <PinIcon />
              </span>
              <span>
                   NEMA HEALTHTECH PRIVATE LIMITED, Ground floor, 140, sector 38, Medicity, Islampur Village,
                Gurugram, Haryana 122022
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="site-footer__bottom">
        <div className="site-footer__bottom-inner">
          <p>© 2025 NEMA Care. All rights reserved.</p>
          <p className="site-footer__trust">
            <span className="site-footer__trust-icon" aria-hidden>
              <ShieldIcon />
            </span>
            NCID Identity Verification <span aria-hidden>·</span> Direct Hiring <span aria-hidden>·</span> Transparent Fees
          </p>
        </div>
      </div>
    </footer>
  );
}
